import React, {memo, useEffect, useRef, useState} from 'react';
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Link from "@/Layouts/Link";
import Svg from "@/Components/Svg";
import Video from "@/Components/Video";
import Picture from "@/Components/Picture";
import '@/Scss/Site/Main/mainCarousel.scss';

export default function MainCarousel(props) {
    const {mainSliders = [], isMobile} = props;
    const [scrollProgress, setScrollProgress] = useState(0);

    const autoplay = useRef(null);

    if (!autoplay.current && mainSliders.length > 1) {
        autoplay.current = Autoplay({
            delay: 5000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
            stopOnFocusIn: true
        })
    }

    const [carouselRef, emblaApi] = useEmblaCarousel(
        {loop: mainSliders.length > 1},
        autoplay.current ? [autoplay.current] : []
    );

    const goToPrev = () => {
        autoplay.current?.stop();
        emblaApi?.scrollPrev();
        autoplay.current?.play();
    }

    const goToNext = () => {
        autoplay.current?.stop();
        emblaApi?.scrollNext();
        autoplay.current?.play();
    }

    useEffect(() => {
        if (!emblaApi) return;

        const updateProgress = () => {
            const index = emblaApi.selectedScrollSnap();
            const total = emblaApi.scrollSnapList().length - 1;
            const progress = total > 0 ? (index / total) * 100 : 0;

            setScrollProgress(progress);
        }

        updateProgress();

        emblaApi.on('select', updateProgress);

        return () => {
            emblaApi.off('select', updateProgress);
        }
    }, [emblaApi]);

    if (mainSliders.length === 0) return null;

    return (
        <div className={`embla`}>
            <div ref={carouselRef} className={`embla__viewport`}>
                <div className={`embla__container`}>
                    {mainSliders.map(slide => (
                        <SlideItem key={slide.id} className={`embla__slide`} slide={slide} isMobile={isMobile} />
                    ))}
                </div>
            </div>
            {mainSliders.length > 1 && (
                <div className={`embla__controls`}>
                    <div className={`embla__progress`}>
                        <div
                            className={`embla__progressBar`}
                            style={{transform: `translate3d(${scrollProgress}%,0px,0px)`}}
                        />
                    </div>
                    <div className={`embla__buttons`}>
                        <div className={`embla__prev`} onClick={goToPrev}>
                            <Svg id={`arrowLeft`} className={`svg--arrow`} />
                        </div>
                        <div className={`embla__next`} onClick={goToNext}>
                            <Svg id={`arrowRight`} className={`svg--arrow`} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const SlideItem = memo(({className, slide, isMobile}) => {
    const {
        imgPath,
        imgMobilePath,
        video_path,
        video_path_mobile,
        link,
        label
    } = slide;

    const content = video_path && video_path_mobile ? (
        <Video
            poster={isMobile ? imgMobilePath : imgPath}
            source={[isMobile ? video_path_mobile : video_path]}
            controls={false}
            autoPlay
            loop
        />
    ) : (
        <Picture src={imgPath} srcMobile={imgMobilePath} alt={label} />
    );

    const Wrapper = link ? Link : 'div';
    const wrapperProps = link
        ? {href: link}
        : {};

    return (
        <Wrapper className={className} {...wrapperProps}>
            {content}
        </Wrapper>
    );
});
